import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useStore } from "react-redux";
import FilterView from "../../components/trend/filter/FilterView";
import {
  useSystemId, useChartDataList, useLastSocketBatch,
  useSelectedDateTimeFilter, useTimeFilterFrom, useTimeFilterTo,
  useDirectTimestampFrom, useDirectTimestampTo, useDevicesFilterList,
} from "../../hooks/useReduxSelectors";
import DateTimeFilter from "../../components/trend/trendChart/DateTimeFilter";
import config from "config";
import {
  clearAllChartData, fetchUnifiedTrends, appendLiveChartData, getChartKey,
  clearFilterList, setFetchPending, setFetchFulfilled, setFetchRejected,
  restoreDevicesFilterList,
} from "../../ducks/trendsDuck";
import { loadTrendSelections } from "../../utils/trendSelectionsStorage";
import TrendCharts from "../../components/trend/trendsCharts/TrendsCharts";
import { Box, Button, Divider, IconButton, Stack, TextField, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ListIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';

const calculateTrendUnixTimeStamps = (selectedDateTimeFilter, timeFilterFrom, timeFilterTo, directTimestampFrom, directTimestampTo) => {
  // If direct timestamps are available (from datetime pickers), use them directly
  if (directTimestampFrom !== null && directTimestampTo !== null) {
    return {
      "from": directTimestampFrom,
      "to": directTimestampTo
    };
  }

  // Validate input parameters
  if (timeFilterFrom === undefined || timeFilterFrom === null ||
    timeFilterTo === undefined || timeFilterTo === null) {
    // Default to 1 hour range if no valid time filters
    timeFilterFrom = -1;
    timeFilterTo = 0;
  }

  // Otherwise use the original offset-based calculation
  const timeUnit = selectedDateTimeFilter; // "Live/Hour/Day/Week/Month/Year"
  const nowDate = new Date();
  let from, to;

  switch (timeUnit) {
    case "Live":
      from = new Date(nowDate);
      from.setHours(nowDate.getHours() - Math.abs(timeFilterFrom));
      to = new Date(nowDate);
      to.setHours(nowDate.getHours() - Math.abs(timeFilterTo));
      break;
    case "Hour":
      from = new Date(nowDate);
      from.setHours(nowDate.getHours() - Math.abs(timeFilterFrom));
      to = new Date(nowDate);
      to.setHours(nowDate.getHours() - Math.abs(timeFilterTo));
      break;
    case "Day":
      from = new Date(nowDate);
      from.setDate(nowDate.getDate() - Math.abs(timeFilterFrom));
      to = new Date(nowDate);
      to.setDate(nowDate.getDate() - Math.abs(timeFilterTo));
      break;
    case "Week":
      from = new Date(nowDate);
      from.setDate(nowDate.getDate() - 7 * Math.abs(timeFilterFrom));
      to = new Date(nowDate);
      to.setDate(nowDate.getDate() - 7 * Math.abs(timeFilterTo));
      break;
    case "Month":
      from = new Date(nowDate);
      from.setMonth(nowDate.getMonth() - Math.abs(timeFilterFrom));
      to = new Date(nowDate);
      to.setMonth(nowDate.getMonth() - Math.abs(timeFilterTo));
      break;
    case "Year":
      from = new Date(nowDate);
      from.setFullYear(nowDate.getFullYear() - Math.abs(timeFilterFrom));
      to = new Date(nowDate);
      to.setFullYear(nowDate.getFullYear() - Math.abs(timeFilterTo));
      break;
    case "Custom":
    default:
      // Default to 1 hour range for unknown filters or Custom (which should use direct timestamps)
      from = new Date(nowDate);
      from.setHours(nowDate.getHours() - Math.abs(timeFilterFrom));
      to = new Date(nowDate);
      to.setHours(nowDate.getHours() - Math.abs(timeFilterTo));
      break;
  }

  // Validate Date objects before calling getTime()
  if (!from || isNaN(from.getTime()) || !to || isNaN(to.getTime())) {
    // Fallback to current time if dates are invalid
    const now = new Date();
    from = new Date(now.getTime() - 3600000); // 1 hour ago
    to = now;
  }

  return { "from": Math.floor(from.getTime() / 1000), "to": Math.floor(to.getTime() / 1000) };
};



/** Isolated component for live socket → chart data subscription.
 *  Prevents per-second re-renders of the entire TrendContainer. */
const TrendLiveSubscription = ({ systemId }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const lastSocketBatch = useLastSocketBatch();

  useEffect(() => {
    if (!lastSocketBatch) return;
    const trends = store.getState().historical[systemId]?.trends;
    if (!trends || trends.selectedDateTimeFilter !== "Live") return;
    lastSocketBatch.forEach((dataObject) => {
      const chartKey = getChartKey(dataObject.HwId, dataObject.ValueName, trends.devicesFilterList);
      if (!chartKey) return;
      const isLiveStarted =
        trends.devicesLiveList[dataObject.HwId]?.[dataObject.ValueName];
      if (isLiveStarted) {
        dispatch(appendLiveChartData({ dataObject, chart: chartKey, systemId }));
      }
    });
  }, [lastSocketBatch, dispatch, systemId, store]);

  return null;
};


const TrendContainer = () => {

  const dispatch = useDispatch();
  const store = useStore();
  const isMobile = useMediaQuery('(max-width:700px)');
  const theme = useTheme();

  const [filter, setFilter] = useState("");


  const systemId = useSystemId();
  const chartData = useChartDataList();
  const devicesFilterList = useDevicesFilterList() || {};
  const selectedDateTimeFilter = useSelectedDateTimeFilter() || "Live";
  const timeFilterFrom = useTimeFilterFrom() ?? -1;
  const timeFilterTo = useTimeFilterTo() ?? 0;
  const directTimestampFrom = useDirectTimestampFrom() || null;
  const directTimestampTo = useDirectTimestampTo() || null;


  const appEnvironment = config && config.APP_ENV;

  const [hideShow, setHideShow] = useState("show");
  const [chartLayout, setchartLayout] = useState("row");

  const trendRangeFetchRequestTimer = useRef(null); // Timer for trend fetch with range selected
  const prevDevicesFilterListRef = useRef({}); // Reference to previous devicesFilterList
  const hasRestoredSelectionsRef = useRef(false); // Guards the one-shot localStorage rehydration
  const prevTimeParamsRef = useRef({
    from: timeFilterFrom,
    to: timeFilterTo,
    selected: selectedDateTimeFilter,
    directFrom: directTimestampFrom,
    directTo: directTimestampTo
  }); // Reference to previous time parameters
  const abortControllerRef = useRef(null);   // Reference to fetch AbortController


  // Function to find newly added filter values
  const findNewValues = (currentList, prevList) => {
    let newValues = {};
    for (const chart in currentList) {
      for (const device in currentList[chart]) {
        for (const value in currentList[chart][device]) {
          const isNewChart = !prevList[chart];
          const isNewDevice = isNewChart || !prevList[chart][device];
          const isNewValue = isNewDevice || !prevList[chart][device][value];

          if (isNewValue) {
            if (!newValues[chart]) newValues[chart] = {};
            if (!newValues[chart][device]) newValues[chart][device] = {};
            newValues[chart][device][value] = currentList[chart][device][value];
          }
        }
      }
    }
    return newValues;
  };

  // Function to count value objects in the devicesFilterList
  const countValueObjects = (list) => {
    let totalCount = 0;

    // Iterate over each chart in the list
    for (const chartKey in list) {
      const chart = list[chartKey];

      // Iterate over each device in the chart
      for (const deviceKey in chart) {
        const device = chart[deviceKey];

        // Count each value object in the device
        totalCount += Object.keys(device).length;
      }
    }

    return totalCount;
  };


  const createLiveFetchParam = (hwId, key) => ({
    SiteId: "",
    EdgeUnitId: "",
    HwId: hwId,
    ValueName: key,
    Timespan: "Live",
    TimeMultiplier: 1,
  });

  const createRangeFetchParam = (hwId, key, fromAndToUnixTimestamps) => ({
    SiteId: "",
    EdgeUnitId: "",
    HwId: hwId,
    ValueName: key,
    From: fromAndToUnixTimestamps.from,
    To: fromAndToUnixTimestamps.to,
  });

  function isDevicesFilterListEmpty(devicesFilterList) {
    return Object.keys(devicesFilterList).every(key => !devicesFilterList[key] || Object.keys(devicesFilterList[key]).length === 0);
  }

  const getTrendsData = useCallback((forceReload) => {
    // If filterlist is empty, return  
    if (isDevicesFilterListEmpty(devicesFilterList)) {
      return;
    }


    const newValues = findNewValues(devicesFilterList, prevDevicesFilterListRef.current);
    const timeParamsChanged = prevTimeParamsRef.current.from !== timeFilterFrom ||
      prevTimeParamsRef.current.to !== timeFilterTo ||
      prevTimeParamsRef.current.selected !== selectedDateTimeFilter ||
      prevTimeParamsRef.current.directFrom !== directTimestampFrom ||
      prevTimeParamsRef.current.directTo !== directTimestampTo;

    if (!forceReload) {
      if ((!newValues || Object.keys(newValues).length === 0) && !timeParamsChanged) {
        // No new values and time parameters didn't change, return
        return;
      }
    }

    // Abort previous requests if user selected different time window
    if ((abortControllerRef.current) && (timeParamsChanged)) {
      console.log("aborted previous fetches, new fetches initiated");
      abortControllerRef.current.abort();
    }
    // Create a new AbortController for the new requests
    abortControllerRef.current = new AbortController();

    // If new values were added, fetch them, otherwise fetch all values
    const listToFetch = (!newValues || Object.keys(newValues).length === 0) ? devicesFilterList : newValues;

    Object.keys(listToFetch).forEach((chart) => {
      Object.keys(listToFetch[chart]).forEach((device) => {
        Object.keys(listToFetch[chart][device]).forEach((key) => {
          // Dispatch request with param and abort signal
          const fromAndToUnixTimestamps = calculateTrendUnixTimeStamps(selectedDateTimeFilter, timeFilterFrom, timeFilterTo, directTimestampFrom, directTimestampTo);

          // Use range format if we have direct timestamps, otherwise use Live format for Live filter
          const param = (directTimestampFrom !== null && directTimestampTo !== null)
            ? createRangeFetchParam(device, key, fromAndToUnixTimestamps)
            : (selectedDateTimeFilter === "Live" ? createLiveFetchParam(device, key) : createRangeFetchParam(device, key, fromAndToUnixTimestamps));

          // Use unified trend fetching approach
          dispatch(setFetchPending({ systemId, chart, HwId: device, ValueName: key }));
          dispatch(fetchUnifiedTrends({
            systemId,
            param,
            filter: "",
            chart,
            signal: abortControllerRef.current.signal,
            minVal: timeFilterFrom,
            maxVal: timeFilterTo,
            selectedDateTimeFilter
          }))
            .unwrap()
            .then((result) => {
              dispatch(setFetchFulfilled({ systemId, chart, HwId: device, ValueName: key, response: result.response }));
            })
            .catch((err) => {
              if (err?.error?.message === "canceled") return;
              dispatch(setFetchRejected({
                systemId,
                chart,
                HwId: device,
                ValueName: key,
                errorMessage: err?.error?.message || err?.message,
                httpResponseStatus: err?.error?.status || err?.status,
                requiresLoading: err?.error?.requiresLoading,
              }));
            });
        });
      });
    });

    // Update references after dispatching the actions
    prevDevicesFilterListRef.current = structuredClone(devicesFilterList);
    prevTimeParamsRef.current = {
      from: timeFilterFrom,
      to: timeFilterTo,
      selected: selectedDateTimeFilter,
      directFrom: directTimestampFrom,
      directTo: directTimestampTo
    };
  }, [devicesFilterList, selectedDateTimeFilter, timeFilterFrom, timeFilterTo, directTimestampFrom, directTimestampTo, systemId]);

  const handleHide = useCallback(() => {
    setHideShow(hideShow === "show" ? "hide" : "show");
  }, [hideShow]);

  const handleChartPosition = () => {
    if (chartLayout === "row") {
      setchartLayout("column");
    } else {
      setchartLayout("row");
    }
  };

  const clearChartSelections = () => {
    dispatch(clearFilterList({ systemId }));
    dispatch(clearAllChartData({ systemId }));
    // Clear previous references
    prevDevicesFilterListRef.current = {};
  }

  // Rehydrate chart selections persisted under "Trends_<systemId>"
  useEffect(() => {
    if (!systemId || hasRestoredSelectionsRef.current) return;
    hasRestoredSelectionsRef.current = true;
    const current = store.getState().historical?.[systemId]?.trends?.devicesFilterList || {};
    if (!isDevicesFilterListEmpty(current)) return;
    const stored = loadTrendSelections(systemId);
    if (!stored) return;
    dispatch(restoreDevicesFilterList({ systemId, devicesFilterList: stored }));
  }, [systemId, store, dispatch]);

  useEffect(() => {
    getTrendsData();
  }, [timeFilterFrom, timeFilterTo, directTimestampFrom, directTimestampTo, devicesFilterList]);

  // Update the prevDevicesFilterListRef when a value is removed from the devicesFilterList
  useEffect(() => {
    const currentValueCount = countValueObjects(devicesFilterList);
    const prevValueCount = countValueObjects(prevDevicesFilterListRef.current || {});

    if (currentValueCount < prevValueCount) {
      prevDevicesFilterListRef.current = structuredClone(devicesFilterList);
    }
  }, [devicesFilterList]);

  // Trigger long trend pre load when opening trend view - DISABLED
  // useEffect(() => {
  //   dispatch(triggerTrendLongPreLoad({ systemId: systemId, SiteId: "", DeviceId: systemId }));
  // }, []);

  return (
    <>
      <TrendLiveSubscription systemId={systemId} />
      <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0, minWidth: 0 }}>
        {/* Top controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            flexDirection: isMobile ? "column" : "row",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack sx={{ mb: { xs: 1, sm: 1, md: 0 }, width: isMobile ? "100%" : "auto" }}>
            <Box sx={{
              display: "flex", flexDirection: "row", gap: 2, flexWrap: "wrap", minWidth: { md: "250px" }, justifyContent: isMobile && "center"
            }}>
              <Tooltip title="Toggle Filter Panel">
                <Button
                  onClick={handleHide}
                  variant="outlined"
                  size="small"
                >
                  {hideShow === "hide" ? <VisibilityOffIcon color="error" /> : <VisibilityIcon />}
                  <ListIcon sx={{ color: theme.palette.text.primary }} />
                </Button>
              </Tooltip>
              <Tooltip title="Toggle Chart Layout">
                <Button
                  onClick={handleChartPosition}
                  variant="outlined"
                  size="small"
                >
                  {chartLayout === "row" ? <ViewColumnIcon sx={{ color: theme.palette.text.primary }} /> : <TableRowsIcon sx={{ color: theme.palette.text.primary }} />}
                </Button>
              </Tooltip>
              <Tooltip title="Clear All Chart Selections">
                <Button
                  onClick={clearChartSelections}
                  variant="outlined"
                  size="small"
                >
                  <FilterListOffIcon sx={{ color: theme.palette.text.primary }} />
                </Button>
              </Tooltip>
            </Box>
            {!isMobile &&
              <Box sx={{ display: "flex", alignItems: "center", mb: { xs: 1, sm: 1, md: 0 } }}>
                <SearchIcon size="small" sx={{ color: "action.active", mr: 1, mt: 2 }} />
                <TextField
                  label="Search"
                  variant="standard"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  fullWidth
                />
              </Box>
            }
          </Stack>
          <DateTimeFilter appEnvironment={appEnvironment} getTrendsData={getTrendsData} />
          {isMobile &&
            <Box sx={{ display: "flex", alignItems: "center", mb: { xs: 1, sm: 1, md: 0 } }}>
              <SearchIcon size="small" sx={{ color: "action.active", mr: 1, mt: 2 }} />
              <TextField
                label="Search"
                variant="standard"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                fullWidth
              />
            </Box>
          }
        </Box>

        {/* Main content */}
        <Box sx={{ display: "flex", overflow: "hidden", flexGrow: 1, minWidth: 0, minHeight: 0 }}>
          {hideShow === "show" && (
            <Box
              sx={{
                flex: isMobile ? "1 1 100%" : "0 0 30%",
                width: isMobile ? "100%" : "30%",
                minWidth: 0,
                maxWidth: isMobile ? "100%" : "30%",
                pr: 2,
                borderRight: "1px solid",
                borderColor: "divider",
                maxHeight: "100%",
                overflow: "hidden"
              }}
            >
              <FilterView filter={filter} />
            </Box>
          )}

          <Box sx={{ flexGrow: 1, minWidth: 0, overflowY: "auto", overflowX: "hidden", height: "100%", minHeight: 0 }}>
            <TrendCharts chartData={chartData} chartLayout={chartLayout} />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default TrendContainer;
